const API_URL_FALLBACK_LOCAL = 'http://localhost:8000/api/v1';
const VALID_URL_PREFIX = /^https?:\/\//i;

export function normalizeApiUrl(rawUrl: string | undefined): string | null {
  if (!rawUrl) {
    return null;
  }

  const urlText = rawUrl.trim();
  if (!urlText || !VALID_URL_PREFIX.test(urlText)) {
    return null;
  }

  try {
    const parsed = new URL(urlText);
    const normalized = parsed.toString().replace(/\/+$/g, '');
    return normalized;
  } catch {
    return null;
  }
}

const getApiBaseUrl = (): string => {
  const explicitUrl = normalizeApiUrl(process.env.REACT_APP_API_BASE_URL);
  if (explicitUrl) {
    return explicitUrl;
  }

  if (process.env.NODE_ENV === 'production') {
    console.error('REACT_APP_API_BASE_URL is required in production builds');
  }

  return API_URL_FALLBACK_LOCAL;
};

export const API_URL = getApiBaseUrl();

export const API_ENDPOINTS = {
  // Auth
  SIGNIN: '/user_login/',
  LOGOUT: '/auth/logout/',
  FORGOT_PASSWORD: '/forgot-password/',
  RESET_PASSWORD: '/password/reset_password/',
  CHANGE_PASSWORD: (userId: string) => `/password/${userId}/change_password/`,
  SIGNUP: '/signup/',
  AZURE_LOGIN: '/auth/azure/login/',
  AZURE_CONFIG: '/auth/azure/config/',
  AZURE_START: '/auth/azure/start/',
  VERIFY_EMAIL: '/verify-email/',
  AUTH_SESSION: '/auth/session/',

  // Data Flows
  CREATE_DATA_FLOW: '/files/create_file/',
  GET_DATA_FLOWS: '/files/get_files/',
  GET_DATA_FLOW: '/files/get_file/',
  DELETE_DATA_FLOW: '/files/delete_file/',
  RENAME_DATA_FLOW: '/files/rename_file/',
  SET_OUTPUT_SOURCE_SETTINGS: '/files/set_output_datastore_settings/',
  SCHEDULE_DATA_FLOW: '/files/schedule_file/',
  UNSCHEDULE_DATA_FLOW: '/files/unschedule_file/',
  RUN_DATA_FLOW: '/files/run_file/',
  GET_SCHEDULE_HISTORY: '/files/get_schedule_history/',

  // Reference Data
  CREATE_REFERENCE_DATA: '/files/create_list/',
  GET_REFERENCE_DATA: '/files/get_lists/',
  GET_REFERENCE_DATA_ITEM: '/files/get_list/',
  GET_REFERENCE_DATA_HEADERS: '/files/get_list_headers/',
  DELETE_REFERENCE_DATA: '/files/delete_list/',
  RENAME_REFERENCE_DATA: '/files/rename_file/',
  UPDATE_REFERENCE_DATA: '/files/update_data/',

  // Actions
  CREATE_ACTION: '/jobs/create_job/',
  GET_ACTIONS: '/jobs/get_jobs/',
  DELETE_ACTION: '/jobs/delete_job/',
  RUN_ACTION: '/jobs/run_jobs/',

  // Profile
  GET_PROFILE: (userId: string) => `/users/${userId}/get_profile/`,
  UPDATE_PROFILE: (userId: string) => `/users/${userId}/update_profile/`,
  DELETE_ACCOUNT: (userId: string) => `/users/${userId}/delete_account/`,

  // Presets
  GET_PRESETS: '/presets/get_presets/',
  CREATE_PRESET: '/presets/create_preset/',
  APPLY_PRESET: '/presets/apply_preset/',
  DELETE_PRESET: '/presets/delete_preset/',
  RENAME_PRESET: '/presets/rename_preset/',

  // Sources
  CREATE_SOURCE: '/datastores/create_datastore/',
  GET_SOURCES: '/datastores/get_datastores/',
  DELETE_SOURCE: '/datastores/delete_datastore/',
  CHECK_SOURCE: '/datastores/check_datastore/',
  RENAME_SOURCE: '/datastores/rename_datastore/',
  UPDATE_SOURCE: '/datastores/update_datastore/',
  TEST_SOURCE: '/datastores/test/',
  ANALYZE_SOURCE: '/datastores/analyze/',
  SUGGEST_PROTOCOL: '/datastores/suggest-protocol/',
  CREATE_FROM_SOURCE: '/files/create_from_datastore/',
  CREATE_SCHEMA_BUNDLE: '/datastores/schema-bundles/',
  GET_SCHEMA_BUNDLES: '/datastores/schema-bundles/',
  DELETE_SCHEMA_BUNDLE: '/datastores/schema-bundles/',
  GET_SCHEMA_BUNDLE: (bundleId: number) => `/datastores/schema-bundles/${bundleId}/`,

  // AI Module Builder
  AI_GENERATE_PIPELINE: '/ai/generate-pipeline/',
  AI_SUGGEST_MODULES: '/ai/suggest-modules/',
  AI_VALIDATE_PIPELINE: '/ai/validate-pipeline/',
  AI_OPERATIONS: '/ai/operations/',
  AI_FEEDBACK: '/ai/feedback/',
  AI_USAGE: '/ai/usage/',

  // Results
  GET_RESULTS: '/files/get_results/',

  // Users
  GET_USERS: '/users/get_users/',
  DELETE_USER: '/users/delete_user/',

  // Departments (backend aliases /organizations/* for one release; departments/* is canonical)
  GET_DEPARTMENTS: '/departments/get_departments/',
  CREATE_DEPARTMENT: '/departments/create_department/',
  RENAME_DEPARTMENT: '/departments/rename_department/',
  DELETE_DEPARTMENT: '/departments/delete_department/',
  SET_DEFAULT_DEPARTMENT: '/departments/set_default/',
  ADD_USER_TO_DEPARTMENT: '/departments/add_user/',
  REMOVE_USER_FROM_DEPARTMENT: '/departments/remove_user/',

  // Department Settings
  GET_DEPARTMENT_SETTINGS: (deptId: number) => `/departments/${deptId}/settings/`,
  UPDATE_DEPARTMENT_SETTINGS: (deptId: number) => `/departments/${deptId}/settings/update/`,
  GET_AVAILABLE_PLUGINS: (deptId: number) => `/departments/${deptId}/plugins/`,
  MANAGE_PLUGINS: (deptId: number) => `/departments/${deptId}/plugins/manage/`,
  CONFIGURE_ALERTS: (deptId: number) => `/departments/${deptId}/alerts/config/`,
  SET_COST_CONTROL: (deptId: number) => `/departments/${deptId}/cost-control/`,

  // Billing (Stripe Checkout)
  BILLING_CREATE_CHECKOUT_SESSION: '/billing/create-checkout-session/',
  BILLING_STATUS: '/billing/status/',
  BILLING_PLANS_PRICING: '/billing/plans-pricing/',
  BILLING_CREATE_SUBSCRIPTION: '/billing/create-subscription/',
  BILLING_SUBSCRIBE_ANNUAL: '/billing/subscribe-annual/',
  BILLING_CHANGE_PLAN: '/billing/change-plan/',
  BILLING_CUSTOMER_PORTAL: '/billing/customer-portal/',
  BILLING_PAYMENTS: '/billing/payments/',
  BILLING_INVOICE: '/billing/invoice/',
  BILLING_TRIAL: '/billing/trial/',
  WORKSPACE_SEATS: '/workspace/seats/',

  // Admin
  ADMIN_BILLING_SUMMARY: '/admin/billing-summary/',
  ADMIN_WORKSPACES: '/admin/workspaces/',

  // System
  GET_SYSTEM_HEALTH: '/system/health/',
  GET_SYSTEM_LOGS: '/system/logs/',
  GET_SYSTEM_REPORTING: '/system/reporting/',
  CLEAR_TEMP_STORAGE: '/system/tmp/clear/'
};
